'use client';

/**
 * RegionMiniMap — a small, non-interactive Google map on a region page showing
 * OUR region outline (from /api/regions/<slug>/map), zoomed to the region. The
 * whole thing links into the explorer's region view (`?region=<slug>`) so it
 * opens in our maps, not Google Maps. Falls back to a centred pin if the region
 * has no boundary (e.g. highway corridors).
 */

import { withBase } from '../lib/brand';
import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { getGoogleMapsLoader } from '../lib/googleMapsLoader';
import { useCaPreview } from '../lib/caPreview';
import PlaceMapTile from './store/PlaceMapTile';

interface RegionMiniMapProps {
  slug: string;
  regionName: string;
  lat?: number | null;
  lng?: number | null;
  height?: number;
}

export default function RegionMiniMap({ slug, regionName, lat, lng, height = 240 }: RegionMiniMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  // The map sits well below the fold on campsite and region pages, but used
  // to pull the whole Google Maps API (~1 MB of script) and the region outline
  // (~120 KB) at page load, competing with the hero (trace, 5 Oct 2026). Wait
  // until it is about to scroll into view.
  const [nearView, setNearView] = useState(false);
  // ca_preview: the map is a picture (it never took gestures anyway), from
  // our own tile server, so it is never an empty grey box while Google loads.
  const preview = useCaPreview();

  useEffect(() => {
    const el = mapRef.current;
    if (!el || nearView || preview) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNearView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNearView(true);
          io.disconnect();
        }
      },
      { rootMargin: '400px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [nearView, preview]);

  useEffect(() => {
    if (!nearView) return;
    let cancelled = false;
    (async () => {
      try {
        let boundary: any = null;
        try {
          const resp = await fetch(`https://app.trip-planner.com.au/api/regions/${slug}/map`);
          if (resp.ok) boundary = (await resp.json()).boundary;
        } catch {/* fall back to centred pin */}

        // Other components on a page (e.g. the campsite Map tab) may have already
        // loaded Google Maps via a different loader instance; reusing the shared
        // loader then throws "Loader must not be called again with different
        // options". So only load if maps isn't present, and tolerate a loader
        // conflict as long as the API ended up available.
        const w = window as unknown as { google?: { maps?: unknown } };
        if (!w.google?.maps) {
          try {
            await getGoogleMapsLoader().load();
          } catch (e) {
            if (!w.google?.maps) throw e;
          }
        }
        if (cancelled || !mapRef.current) return;

        const map = new google.maps.Map(mapRef.current, {
          disableDefaultUI: true,
          gestureHandling: 'none',
          keyboardShortcuts: false,
          clickableIcons: false,
          center: { lat: lat ?? -25.3, lng: lng ?? 133.8 },
          zoom: 7,
        });

        if (boundary) {
          const layer = new google.maps.Data({ map });
          layer.setStyle({
            fillColor: '#1a68d1', fillOpacity: 0.12,
            strokeColor: '#1a68d1', strokeOpacity: 0.95, strokeWeight: 2,
            clickable: false,
          });
          layer.addGeoJson({ type: 'Feature', geometry: boundary, properties: {} });
          const bounds = new google.maps.LatLngBounds();
          layer.forEach((f) => f.getGeometry()?.forEachLatLng((ll) => bounds.extend(ll)));
          if (!bounds.isEmpty()) map.fitBounds(bounds, 16);
        } else if (lat != null && lng != null) {
          new google.maps.Marker({ map, position: { lat, lng } });
          map.setCenter({ lat, lng });
          map.setZoom(8);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [nearView, slug, lat, lng]);

  if (preview) {
    return (
      <Link
        href={withBase(`/?region=${slug}`)}
        aria-label={`Explore ${regionName} on the map`}
        className="ca-minimap-static block relative group"
        style={{ height }}
      >
        <PlaceMapTile
          lat={lat ?? -25.3}
          lng={lng ?? 133.8}
          width={420}
          height={height}
          zoom={lat != null && lng != null ? 7 : 3}
          eager
          className="absolute inset-0"
        />
        <span className="ca-minimap-static__cta">Explore {regionName} on the map →</span>
      </Link>
    );
  }

  // If the map can't initialise, still give a useful link into the explorer.
  if (failed) {
    return (
      <Link
        href={withBase(`/?region=${slug}`)}
        className="flex items-center justify-center gap-2 bg-slate-50 text-[#1558b0] font-semibold text-sm hover:bg-[#e3f2fd] transition-colors"
        style={{ height }}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
        View {regionName} on the map →
      </Link>
    );
  }

  return (
    <Link
      href={withBase(`/?region=${slug}`)}
      aria-label={`Explore ${regionName} on the map`}
      className="block relative group"
    >
      <div ref={mapRef} style={{ width: '100%', height }} className="ca-minimap-canvas bg-gray-100" />
      <div className="absolute inset-0 flex items-end justify-center pb-3 pointer-events-none bg-gradient-to-t from-black/25 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="text-xs font-semibold text-white bg-black/55 rounded-full px-3 py-1 backdrop-blur-sm">
          Explore {regionName} on the map →
        </span>
      </div>
    </Link>
  );
}
